import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { ISyncQueueItem, SocDiscipline } from "@home-health-notify/models";
import CommonDialog from "../common/CommonDialog/CommonDialog";
import { SocStarterField } from "../addPatientGroup/SocStarterField";
import { patientName, servicesSummary } from "./syncQueueUtils";

type ApproveConfirmDialogProps = {
  item: ISyncQueueItem | null;
  isSubmitting: boolean;
  errorReason: string | null;
  onConfirm: (socDiscipline: SocDiscipline) => void;
  onClose: () => void;
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

export default function ApproveConfirmDialog({
  item,
  isSubmitting,
  errorReason,
  onConfirm,
  onClose,
}: ApproveConfirmDialogProps) {
  const [socDiscipline, setSocDiscipline] = useState<SocDiscipline>("SN");

  // Reset to the default (SN) each time a different row's dialog opens.
  useEffect(() => {
    if (item) setSocDiscipline("SN");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id]);

  return (
    <CommonDialog
      isDialogOpen={item !== null}
      onDialogClose={onClose}
      title="Create this patient in HHN?"
      maxWidth="xs"
      actionContent={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            data-testid="approve-confirm-cancel"
          >
            {errorReason ? "Close" : "Cancel"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onConfirm(socDiscipline)}
            disabled={isSubmitting || errorReason !== null}
            data-testid="approve-confirm-submit"
          >
            Approve &amp; create
          </Button>
        </>
      }
    >
      {item && (
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
            This creates a new patient record in Home Health Notify from the EMR
            data below. This can&rsquo;t be undone.
          </Typography>
          <Box
            sx={theme => ({
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1,
              px: 1.5,
              py: 1,
            })}
          >
            <SummaryRow label="Patient" value={patientName(item)} />
            <SummaryRow
              label="Date of Birth"
              value={item.payload?.patientInfo?.dob || "—"}
            />
            <SummaryRow label="MRN" value={item.emrId} />
            <SummaryRow label="Services" value={servicesSummary(item) || "—"} />
          </Box>
          {!errorReason && (
            <Box sx={{ mt: 1.5 }}>
              <SocStarterField
                socStarter={socDiscipline}
                setRole={value => setSocDiscipline(value as SocDiscipline)}
                patientStatus={item.payload?.patientStatus ?? 1}
              />
            </Box>
          )}
          {errorReason && (
            <Typography
              variant="body2"
              data-testid="approve-confirm-error"
              sx={theme => ({
                mt: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                backgroundColor: `${theme.palette.error.main}1a`,
                color: theme.palette.error.dark,
              })}
            >
              Couldn&rsquo;t create patient: {errorReason}
            </Typography>
          )}
        </Box>
      )}
    </CommonDialog>
  );
}
